import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Edit, Trash, Search, Check, X, Filter, Package, Calendar, Truck, TrendingUp, DollarSign, Eye, FileText, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';

const QuanLyLoManagement = () => {
    // State Management
    const [quanLyLos, setQuanLyLos] = useState([]);
    const [nhaCungCaps, setNhaCungCaps] = useState([]);
    const [nhapBaoBiData, setNhapBaoBiData] = useState([]);
    const [thuChiData, setThuChiData] = useState([]);
    const [currentQuanLyLo, setCurrentQuanLyLo] = useState({
        'Số lô': '',
        'Ngày mua': '',
        'Nhà cung cấp': '',
        'Khối lượng dự kiến (tấn)': '',
        'Ghi chú': ''
    });
    const [showModal, setShowModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [search, setSearch] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'Ngày mua', direction: 'descending' });
    const [isEditMode, setIsEditMode] = useState(false);
    const [dateFilterFrom, setDateFilterFrom] = useState('');
    const [dateFilterTo, setDateFilterTo] = useState('');
    const [nccFilter, setNccFilter] = useState('');
    const [searchNCC, setSearchNCC] = useState('');
    const [detailTab, setDetailTab] = useState('nhapbaobi'); // 'nhapbaobi' or 'thuchi'

    // Fetch data functions
    const fetchQuanLyLos = async () => {
        try {
            const response = await authUtils.apiRequest('QUANLYLO', 'Find', {});
            setQuanLyLos(response);
        } catch (error) {
            console.error('Error fetching quan ly lo list:', error);
            toast.error('Lỗi khi tải danh sách lô');
        }
    };

    const fetchNhaCungCaps = async () => {
        try {
            const response = await authUtils.apiRequestKHO('DSKH', 'Find', {
                "PHAN_LOAI": "NCC"
            });
            setNhaCungCaps(response);
        } catch (error) {
            console.error('Error fetching nha cung cap list:', error);
            toast.error('Lỗi khi tải danh sách nhà cung cấp');
        }
    };

    const fetchNhapBaoBi = async () => {
        try {
            const response = await authUtils.apiRequest('NHAPBAOBI', 'Find', {});
            setNhapBaoBiData(response);
        } catch (error) {
            console.error('Error fetching nhap bao bi data:', error);
            toast.error('Lỗi khi tải dữ liệu nhập bao bì');
        }
    };

    const fetchThuChi = async () => {
        try {
            const response = await authUtils.apiRequest('THUCHI_THEOLO', 'Find', {});
            setThuChiData(response);
        } catch (error) {
            console.error('Error fetching thu chi data:', error);
            toast.error('Lỗi khi tải dữ liệu thu chi');
        }
    };

    useEffect(() => {
        fetchQuanLyLos();
        fetchNhaCungCaps();
        fetchNhapBaoBi();
        fetchThuChi();
    }, []);

    // Hàm tính toán các cột ảo - Sử dụng THỰC NHẬN BB (KG)
    const calculateVirtualColumns = (soLo, loItem, nhapBySoLo, thuChiBySoLo) => {
        const nhapBaoBiFiltered = nhapBySoLo.get(soLo) || [];

        // Tính khối lượng thực tế từ THỰC NHẬN BB (KG) (từ KG sang TẤN)
        const khoiLuongThucTe = nhapBaoBiFiltered.reduce((sum, item) => {
            const thucNhanBB = parseFloat(item['THỰC NHẬN BB (KG)']) || 0;
            return sum + thucNhanBB;
        }, 0) / 1000; // Chuyển từ KG sang TẤN

        // Lấy khối lượng dự kiến từ QUANLYLO
        const khoiLuongDuKien = parseFloat(loItem?.['Khối lượng dự kiến (tấn)']) || 0;

        // Tính % đạt khối lượng
        const phanTramDatKhoiLuong = khoiLuongDuKien > 0
            ? (khoiLuongThucTe / khoiLuongDuKien) * 100
            : 0

        const thuChiFiltered = thuChiBySoLo.get(soLo) || [];

        const tongChi = thuChiFiltered
            .filter(item => item['Nghiệp vụ'] === 'Chi')
            .reduce((sum, item) => sum + (parseFloat(item['Số tiền']) || 0), 0);

        const tongThu = thuChiFiltered
            .filter(item => item['Nghiệp vụ'] === 'Thu')
            .reduce((sum, item) => sum + (parseFloat(item['Số tiền']) || 0), 0);

        const loiNhuan = tongThu - tongChi;
        const phanTramLoiNhuan = tongThu > 0 ? (loiNhuan / tongThu) * 100 : 0;

        return {
            khoiLuongThucTe,
            phanTramDatKhoiLuong,
            tongChi,
            tongThu,
            loiNhuan,
            phanTramLoiNhuan
        };
    };

    // Chỉ tính lại khi dữ liệu đổi — trước đây mỗi lần render (mỗi phím gõ) đều quét
    // toàn bộ nhập bao bì + thu chi cho từng lô.
    const enrichedQuanLyLos = useMemo(() => {
        const groupBy = (list, field) => {
            const map = new Map();
            for (const item of list) {
                const key = item[field];
                if (!map.has(key)) map.set(key, []);
                map.get(key).push(item);
            }
            return map;
        };
        const nhapBySoLo = groupBy(nhapBaoBiData, 'SỐ LÔ');
        const thuChiBySoLo = groupBy(thuChiData, 'Số lô');
        // Giữ đúng hành vi cũ của .find(): lấy dòng ĐẦU TIÊN có cùng số lô
        const loBySoLo = new Map();
        for (const lo of quanLyLos) {
            if (!loBySoLo.has(lo['Số lô'])) loBySoLo.set(lo['Số lô'], lo);
        }

        return quanLyLos.map(item => {
            const soLo = item['Số lô'];
            const virtualData = calculateVirtualColumns(soLo, loBySoLo.get(soLo), nhapBySoLo, thuChiBySoLo);
            return {
                ...item,
                ...virtualData
            };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quanLyLos, nhapBaoBiData, thuChiData]);

    const generateSoLo = (ngayMua, tenNCC) => {
        if (!ngayMua || !tenNCC) return '';

        const ncc = nhaCungCaps.find(n => n.TEN_KHACHHANG === tenNCC);
        if (!ncc) return '';

        const tenVietTat = ncc.TEN_VIET_TAT || '';

        const date = new Date(ngayMua);
        const yy = date.getFullYear().toString().slice(-2);
        const mm = (date.getMonth() + 1).toString().padStart(2, '0');
        const dd = date.getDate().toString().padStart(2, '0');
        const dateStr = `${yy}${mm}${dd}`;

        const prefix = `LO-${dateStr}-${tenVietTat}-`;
        const existingLos = quanLyLos.filter(lo =>
            lo['Số lô'] && lo['Số lô'].startsWith(prefix)
        );

        let maxNumber = 0;
        existingLos.forEach(lo => {
            const numberPart = lo['Số lô'].split('-').pop();
            const num = parseInt(numberPart);
            if (!isNaN(num) && num > maxNumber) {
                maxNumber = num;
            }
        });

        const nextNumber = (maxNumber + 1).toString().padStart(4, '0');
        return `LO-${dateStr}-${tenVietTat}-${nextNumber}`;
    };

    const getSortedItems = useCallback(() => {
        const sortableItems = [...enrichedQuanLyLos];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                const keyA = a[sortConfig.key];
                const keyB = b[sortConfig.key];

                if (['Khối lượng dự kiến (tấn)', 'khoiLuongThucTe', 'phanTramDatKhoiLuong',
                    'tongChi', 'tongThu', 'loiNhuan', 'phanTramLoiNhuan'].includes(sortConfig.key)) {
                    const numA = parseFloat(keyA) || 0;
                    const numB = parseFloat(keyB) || 0;
                    if (numA < numB) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (numA > numB) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                } else if (sortConfig.key === 'Ngày mua') {
                    const dateA = new Date(keyA || '1900-01-01');
                    const dateB = new Date(keyB || '1900-01-01');
                    if (dateA < dateB) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (dateA > dateB) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                } else {
                    if (keyA < keyB) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (keyA > keyB) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                }
            });
        }
        return sortableItems;
    }, [enrichedQuanLyLos, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const filteredItems = getSortedItems().filter(item => {
        const matchesSearch = (
            item['Số lô']?.toLowerCase().includes(search.toLowerCase()) ||
            item['Nhà cung cấp']?.toLowerCase().includes(search.toLowerCase()) ||
            item['Ghi chú']?.toLowerCase().includes(search.toLowerCase())
        );

        let matchesDateFilter = true;
        if (dateFilterFrom || dateFilterTo) {
            const itemDate = new Date(item['Ngày mua']);

            if (dateFilterFrom && dateFilterTo) {
                const fromDate = new Date(dateFilterFrom);
                const toDate = new Date(dateFilterTo);
                matchesDateFilter = itemDate >= fromDate && itemDate <= toDate;
            } else if (dateFilterFrom) {
                const fromDate = new Date(dateFilterFrom);
                matchesDateFilter = itemDate >= fromDate;
            } else if (dateFilterTo) {
                const toDate = new Date(dateFilterTo);
                matchesDateFilter = itemDate <= toDate;
            }
        }

        const matchesNCCFilter = !nccFilter ||
            item['Nhà cung cấp']?.toLowerCase().includes(nccFilter.toLowerCase());

        return matchesSearch && matchesDateFilter && matchesNCCFilter;
    });

    const filteredNCCs = nhaCungCaps.filter(ncc =>
        ncc.TEN_KHACHHANG.toLowerCase().includes(searchNCC.toLowerCase()) ||
        ncc.TEN_VIET_TAT?.toLowerCase().includes(searchNCC.toLowerCase())
    );

    const uniqueNCCs = [...new Set(quanLyLos.map(item => item['Nhà cung cấp']).filter(Boolean))];

    const statistics = {
        totalLos: filteredItems.length,
        totalNCCs: [...new Set(filteredItems.map(item => item['Nhà cung cấp']).filter(Boolean))].length,
        totalWeight: filteredItems.reduce((sum, item) => sum + (parseFloat(item['Khối lượng dự kiến (tấn)']) || 0), 0),
        totalWeightActual: filteredItems.reduce((sum, item) => sum + (item.khoiLuongThucTe || 0), 0),
        totalRevenue: filteredItems.reduce((sum, item) => sum + (item.tongThu || 0), 0),
        totalProfit: filteredItems.reduce((sum, item) => sum + (item.loiNhuan || 0), 0),
    };

    // Detail Modal handlers
    const handleOpenDetailModal = (item) => {
        setDetailItem(item);
        setDetailTab('nhapbaobi');
        setShowDetailModal(true);
    };

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setDetailItem(null);
        setDetailTab('nhapbaobi');
    };

    // Get detail data for selected item
    const getDetailNhapBaoBi = () => {
        if (!detailItem) return [];
        return nhapBaoBiData.filter(item => item['SỐ LÔ'] === detailItem['Số lô']);
    };

    const getDetailThuChi = () => {
        if (!detailItem) return [];
        return thuChiData.filter(item => item['Số lô'] === detailItem['Số lô']);
    };

    // Modal handlers
    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEditMode(true);
            setCurrentQuanLyLo({
                'Số lô': item['Số lô'] || '',
                'Ngày mua': item['Ngày mua'] || '',
                'Nhà cung cấp': item['Nhà cung cấp'] || '',
                'Khối lượng dự kiến (tấn)': item['Khối lượng dự kiến (tấn)'] || '',
                'Ghi chú': item['Ghi chú'] || ''
            });
        } else {
            setIsEditMode(false);
            const today = new Date().toISOString().split('T')[0];
            setCurrentQuanLyLo({
                'Số lô': '',
                'Ngày mua': today,
                'Nhà cung cấp': '',
                'Khối lượng dự kiến (tấn)': '',
                'Ghi chú': ''
            });
        }
        setSearchNCC('');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
        setSearchNCC('');
        setCurrentQuanLyLo({
            'Số lô': '',
            'Ngày mua': '',
            'Nhà cung cấp': '',
            'Khối lượng dự kiến (tấn)': '',
            'Ghi chú': ''
        });
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleNCCChange = (tenNCC) => {
        const updatedData = {
            ...currentQuanLyLo,
            'Nhà cung cấp': tenNCC
        };

        if (!isEditMode && updatedData['Ngày mua'] && tenNCC) {
            updatedData['Số lô'] = generateSoLo(updatedData['Ngày mua'], tenNCC);
        }

        setCurrentQuanLyLo(updatedData);
        setSearchNCC('');
    };

    const handleDateChange = (date) => {
        const updatedData = {
            ...currentQuanLyLo,
            'Ngày mua': date
        };

        if (!isEditMode && date && updatedData['Nhà cung cấp']) {
            updatedData['Số lô'] = generateSoLo(date, updatedData['Nhà cung cấp']);
        }

        setCurrentQuanLyLo(updatedData);
    };

    const handleInputChange = (field, value) => {
        setCurrentQuanLyLo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateQuanLyLo = (item) => {
        const errors = [];
        if (!item['Số lô']) errors.push('Số lô không được để trống');
        if (!item['Ngày mua']) errors.push('Ngày mua không được để trống');
        if (!item['Nhà cung cấp']) errors.push('Nhà cung cấp không được để trống');
        if (!item['Khối lượng dự kiến (tấn)']) errors.push('Khối lượng dự kiến không được để trống');

        const khoiLuong = parseFloat(item['Khối lượng dự kiến (tấn)']);
        if (isNaN(khoiLuong) || khoiLuong <= 0) {
            errors.push('Khối lượng dự kiến phải là số dương');
        }

        return errors;
    };

    const handleSave = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            const errors = validateQuanLyLo(currentQuanLyLo);
            if (errors.length > 0) {
                toast.error(errors.join('\n'));
                setIsSubmitting(false);
                return;
            }

            if (isEditMode) {
                await authUtils.apiRequest('QUANLYLO', 'Edit', {
                    "Rows": [currentQuanLyLo]
                });
                toast.success('Cập nhật lô thành công!');
            } else {
                await authUtils.apiRequest('QUANLYLO', 'Add', {
                    "Rows": [currentQuanLyLo]
                });
                toast.success('Thêm lô mới thành công!');
            }

            await fetchQuanLyLos();
            await fetchNhapBaoBi();
            await fetchThuChi();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving quan ly lo:', error);
            toast.error('Có lỗi xảy ra: ' + (error.message || 'Không thể lưu lô'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenDeleteConfirmation = (item) => {
        setItemToDelete(item);
        setShowDeleteConfirmation(true);
    };

    const handleCloseDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
        setItemToDelete(null);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;

        try {
            await authUtils.apiRequest('QUANLYLO', 'Delete', {
                "Rows": [{
                    "Số lô": itemToDelete['Số lô']
                }]
            });
            toast.success('Xóa lô thành công!');
            await fetchQuanLyLos();
            handleCloseDeleteConfirmation();
        } catch (error) {
            console.error('Error deleting quan ly lo:', error);
            toast.error('Có lỗi xảy ra khi xóa lô');
        }
    };

    const handleClearFilters = () => {
        setDateFilterFrom('');
        setDateFilterTo('');
        setNccFilter('');
        setSearch('');
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND'
        }).format(value);
    };

    const formatNumber = (value) => {
        return new Intl.NumberFormat('vi-VN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value || 0);
    };

    // JSX Render
    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-5 mb-6 border border-gray-100">
                    {/* Header Section */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Package className="w-7 h-7 text-indigo-600" />
                            Quản lý Lô
                        </h1>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Filter className="w-4 h-4" />
                                {showFilters ? "Ẩn bộ lọc" : "Bộ lọc"}
                            </button>
                            <button
                                onClick={() => handleOpenModal()}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Thêm lô mới
                            </button>
                        </div>
                    </div>

                    {/* Search and Filter Section */}
                    <div className="mb-6 space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Tìm kiếm theo số lô, nhà cung cấp, ghi chú..."
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        {showFilters && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg border">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Từ ngày</label>
                                    <input
                                        type="date"
                                        value={dateFilterFrom}
                                        onChange={(e) => setDateFilterFrom(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Đến ngày</label>
                                    <input
                                        type="date"
                                        value={dateFilterTo}
                                        onChange={(e) => setDateFilterTo(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Lọc theo NCC</label>
                                    <select
                                        value={nccFilter}
                                        onChange={(e) => setNccFilter(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">Tất cả nhà cung cấp</option>
                                        {uniqueNCCs.map(ncc => (
                                            <option key={ncc} value={ncc}>{ncc}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex items-end">
                                    <button
                                        onClick={handleClearFilters}
                                        className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Xóa bộ lọc
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Statistics cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            <h3 className="text-sm text-blue-700 mb-1">Tổng số lô</h3>
                            <p className="text-2xl font-bold text-blue-800">{statistics.totalLos}</p>
                        </div>

                        <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                            <h3 className="text-sm text-green-700 mb-1">Số NCC</h3>
                            <p className="text-2xl font-bold text-green-800">{statistics.totalNCCs}</p>
                        </div>

                        <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                            <h3 className="text-sm text-purple-700 mb-1">KL Dự kiến (Tấn)</h3>
                            <p className="text-xl font-bold text-purple-800">
                                {statistics.totalWeight.toFixed(2)} tấn
                            </p>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                            <h3 className="text-sm text-yellow-700 mb-1">KL Thực tế (Tấn)</h3>
                            <p className="text-xl font-bold text-yellow-800">
                                {statistics.totalWeightActual.toFixed(2)} tấn
                            </p>
                        </div>

                        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                            <h3 className="text-sm text-emerald-700 mb-1">Tổng thu</h3>
                            <p className="text-lg font-bold text-emerald-800">
                                {formatCurrency(statistics.totalRevenue)}
                            </p>
                        </div>

                        <div className="bg-rose-50 border border-rose-100 rounded-lg p-4">
                            <h3 className="text-sm text-rose-700 mb-1">Lợi nhuận</h3>
                            <p className="text-lg font-bold text-rose-800">
                                {formatCurrency(statistics.totalProfit)}
                            </p>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="overflow-x-auto">
                        {filteredItems.length > 0 ? (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            onClick={() => requestSort('Số lô')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Số lô
                                                {sortConfig.key === 'Số lô' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('Ngày mua')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Ngày mua
                                                {sortConfig.key === 'Ngày mua' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('Nhà cung cấp')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Nhà cung cấp
                                                {sortConfig.key === 'Nhà cung cấp' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('Khối lượng dự kiến (tấn)')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                KL Dự kiến (Tấn)
                                                {sortConfig.key === 'Khối lượng dự kiến (tấn)' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('khoiLuongThucTe')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                KL Thực tế (Tấn)
                                                {sortConfig.key === 'khoiLuongThucTe' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('phanTramDatKhoiLuong')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                % Đạt KL
                                                {sortConfig.key === 'phanTramDatKhoiLuong' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('tongChi')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Tổng chi
                                                {sortConfig.key === 'tongChi' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('tongThu')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Tổng thu
                                                {sortConfig.key === 'tongThu' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('loiNhuan')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                Lợi nhuận
                                                {sortConfig.key === 'loiNhuan' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => requestSort('phanTramLoiNhuan')}
                                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        >
                                            <div className="flex items-center gap-1">
                                                % LN
                                                {sortConfig.key === 'phanTramLoiNhuan' && (
                                                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ghi chú
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Thao tác
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredItems.map((item, index) => (
                                        <tr key={`${item['Số lô']}-${index}`} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Package className="w-4 h-4 text-indigo-600" />
                                                    <span className="text-sm font-medium text-gray-900">{item['Số lô']}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-700">
                                                        {new Date(item['Ngày mua']).toLocaleDateString('vi-VN')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Truck className="w-4 h-4 text-green-600" />
                                                    <span className="text-sm text-gray-900">{item['Nhà cung cấp']}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="text-sm font-medium text-purple-700">
                                                    {parseFloat(item['Khối lượng dự kiến (tấn)']).toFixed(2)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="text-sm font-medium text-blue-700">
                                                    {item.khoiLuongThucTe.toFixed(2)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${item.phanTramDatKhoiLuong >= 95 ? 'bg-green-100 text-green-800' :
                                                    item.phanTramDatKhoiLuong >= 90 ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                    {item.phanTramDatKhoiLuong.toFixed(1)}%
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="text-sm font-medium text-red-600">
                                                    {formatCurrency(item.tongChi)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="text-sm font-medium text-green-600">
                                                    {formatCurrency(item.tongThu)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-1">
                                                    <TrendingUp className={`w-4 h-4 ${item.loiNhuan >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                                                    <span className={`text-sm font-bold ${item.loiNhuan >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                        {formatCurrency(item.loiNhuan)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${item.phanTramLoiNhuan >= 20 ? 'bg-green-100 text-green-800' :
                                                    item.phanTramLoiNhuan >= 10 ? 'bg-yellow-100 text-yellow-800' :
                                                        item.phanTramLoiNhuan >= 0 ? 'bg-orange-100 text-orange-800' :
                                                            'bg-red-100 text-red-800'
                                                    }`}>
                                                    {item.phanTramLoiNhuan.toFixed(1)}%
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm text-gray-700 max-w-xs truncate" title={item['Ghi chú']}>
                                                    {item['Ghi chú'] || '—'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center">
                                                <div className="flex justify-center space-x-2">
                                                    <button
                                                        onClick={() => handleOpenDetailModal(item)}
                                                        className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50"
                                                        title="Xem chi tiết"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenModal(item)}
                                                        className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                                        title="Sửa lô"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenDeleteConfirmation(item)}
                                                        className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                                        title="Xóa lô"
                                                    >
                                                        <Trash className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center py-12 bg-white rounded-lg border">
                                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <div className="text-gray-500 text-lg mb-2">Không tìm thấy dữ liệu</div>
                                <div className="text-sm text-gray-400">Không có lô nào phù hợp với tiêu chí tìm kiếm</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Detail Modal */}
            {showDetailModal && detailItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <FileText className="w-6 h-6 text-indigo-600" />
                                    Chi tiết lô: {detailItem['Số lô']}
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Nhà cung cấp: {detailItem['Nhà cung cấp']} |
                                    Ngày mua: {new Date(detailItem['Ngày mua']).toLocaleDateString('vi-VN')}
                                </p>
                            </div>
                            <button
                                onClick={handleCloseDetailModal}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
                                <h3 className="text-sm text-purple-700 mb-1">KL Dự kiến (Tấn)</h3>
                                <p className="text-xl font-bold text-purple-800">
                                    {parseFloat(detailItem['Khối lượng dự kiến (tấn)']).toFixed(2)} tấn
                                </p>
                            </div>
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                                <h3 className="text-sm text-blue-700 mb-1">KL Thực tế (Tấn)</h3>
                                <p className="text-xl font-bold text-blue-800">
                                    {detailItem.khoiLuongThucTe.toFixed(2)} tấn
                                </p>
                            </div>
                            <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                                <h3 className="text-sm text-green-700 mb-1">Tổng thu</h3>
                                <p className="text-lg font-bold text-green-800">
                                    {formatCurrency(detailItem.tongThu)}
                                </p>
                            </div>
                            <div className="bg-rose-50 border border-rose-100 rounded-lg p-4">
                                <h3 className="text-sm text-rose-700 mb-1">Lợi nhuận</h3>
                                <p className="text-lg font-bold text-rose-800">
                                    {formatCurrency(detailItem.loiNhuan)}
                                </p>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="border-b border-gray-200 mb-4">
                            <nav className="-mb-px flex space-x-8">
                                <button
                                    onClick={() => setDetailTab('nhapbaobi')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${detailTab === 'nhapbaobi'
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <Package className="w-4 h-4" />
                                        Nhập bao bì ({getDetailNhapBaoBi().length})
                                    </div>
                                </button>
                                <button
                                    onClick={() => setDetailTab('thuchi')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${detailTab === 'thuchi'
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <DollarSign className="w-4 h-4" />
                                        Thu chi ({getDetailThuChi().length})
                                    </div>
                                </button>
                            </nav>
                        </div>

                        {/* Tab Content */}
                        <div className="overflow-x-auto">
                            {detailTab === 'nhapbaobi' && (
                                <div>
                                    {getDetailNhapBaoBi().length > 0 ? (
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày tháng</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Số xe</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NCC</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nhập BB (kg)</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trừ BB (kg)</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thực nhận BB (kg)</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thực nhận BBA (kg)</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thực nhận BBE (kg)</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {getDetailNhapBaoBi().map((item, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                            {item['NGÀY THÁNG'] ? new Date(item['NGÀY THÁNG']).toLocaleDateString('vi-VN') : '—'}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">{item['SỐ XE'] || '—'}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm">{item['NCC'] || '—'}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 font-medium">
                                                            {formatNumber(item['NHẬP BB (KG)'])}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600">
                                                            {formatNumber(item['TRỪ BB (KG)'])}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 font-semibold">
                                                            {formatNumber(item['THỰC NHẬN BB (KG)'])}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-teal-600 font-semibold">
                                                            {formatNumber(item['THỰC NHẬN BBA (KG)'])}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-pink-600 font-semibold">
                                                            {formatNumber(item['THỰC NHẬN BBE (KG)'])}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="bg-gray-50 font-semibold">
                                                <tr>
                                                    <td colSpan="3" className="px-4 py-3 text-right text-sm">Tổng cộng:</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-700">
                                                        {formatNumber(getDetailNhapBaoBi().reduce((sum, item) => sum + (parseFloat(item['NHẬP BB (KG)']) || 0), 0))} kg
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-red-700">
                                                        {formatNumber(getDetailNhapBaoBi().reduce((sum, item) => sum + (parseFloat(item['TRỪ BB (KG)']) || 0), 0))} kg
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700">
                                                        {formatNumber(getDetailNhapBaoBi().reduce((sum, item) => sum + (parseFloat(item['THỰC NHẬN BB (KG)']) || 0), 0))} kg
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-teal-700">
                                                        {formatNumber(getDetailNhapBaoBi().reduce((sum, item) => sum + (parseFloat(item['THỰC NHẬN BBA (KG)']) || 0), 0))} kg
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-pink-700">
                                                        {formatNumber(getDetailNhapBaoBi().reduce((sum, item) => sum + (parseFloat(item['THỰC NHẬN BBE (KG)']) || 0), 0))} kg
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="3" className="px-4 py-3 text-right text-sm font-bold">Tổng khối lượng thực nhận:</td>
                                                    <td colSpan="5" className="px-4 py-3 whitespace-nowrap text-sm font-bold text-indigo-700">
                                                        {formatNumber(getDetailNhapBaoBi().reduce((sum, item) =>
                                                            sum + (parseFloat(item['THỰC NHẬN BB (KG)']) || 0), 0
                                                        ) / 1000)} tấn
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    ) : (
                                        <div className="text-center py-12">
                                            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                            <p className="text-gray-500">Chưa có dữ liệu nhập bao bì</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {detailTab === 'thuchi' && (
                                <div>
                                    {getDetailThuChi().length > 0 ? (
                                        <div className="space-y-4">
                                            {/* Chi tiết thu */}
                                            <div>
                                                <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                                                    <ArrowUpCircle className="w-5 h-5" />
                                                    Thu ({getDetailThuChi().filter(item => item['Nghiệp vụ'] === 'Thu').length})
                                                </h3>
                                                <table className="min-w-full divide-y divide-gray-200 mb-6">
                                                    <thead className="bg-green-50">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hạng mục</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Số tiền</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Người giao</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ghi chú</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {getDetailThuChi().filter(item => item['Nghiệp vụ'] === 'Thu').map((item, index) => (
                                                            <tr key={index} className="hover:bg-gray-50">
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                                    {item['Ngày'] ? new Date(item['Ngày']).toLocaleDateString('vi-VN') : '—'}
                                                                </td>
                                                                <td className="px-4 py-3 text-sm">{item['Hạng mục'] || '—'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-600">
                                                                    {formatCurrency(parseFloat(item['Số tiền']) || 0)}
                                                                </td>
                                                                <td className="px-4 py-3 text-sm">{item['Người giao'] || '—'}</td>
                                                                <td className="px-4 py-3 text-sm">{item['Ghi chú'] || '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot className="bg-green-50 font-semibold">
                                                        <tr>
                                                            <td colSpan="2" className="px-4 py-3 text-right text-sm">Tổng thu:</td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-green-700">
                                                                {formatCurrency(detailItem.tongThu)}
                                                            </td>
                                                            <td colSpan="2"></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>

                                            {/* Chi tiết chi */}
                                            <div>
                                                <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                                                    <ArrowDownCircle className="w-5 h-5" />
                                                    Chi ({getDetailThuChi().filter(item => item['Nghiệp vụ'] === 'Chi').length})
                                                </h3>
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-red-50">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hạng mục</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Số tiền</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Người giao</th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ghi chú</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {getDetailThuChi().filter(item => item['Nghiệp vụ'] === 'Chi').map((item, index) => (
                                                            <tr key={index} className="hover:bg-gray-50">
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                                    {item['Ngày'] ? new Date(item['Ngày']).toLocaleDateString('vi-VN') : '—'}
                                                                </td>
                                                                <td className="px-4 py-3 text-sm">{item['Hạng mục'] || '—'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-red-600">
                                                                    {formatCurrency(parseFloat(item['Số tiền']) || 0)}
                                                                </td>
                                                                <td className="px-4 py-3 text-sm">{item['Người giao'] || '—'}</td>
                                                                <td className="px-4 py-3 text-sm">{item['Ghi chú'] || '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot className="bg-red-50 font-semibold">
                                                        <tr>
                                                            <td colSpan="2" className="px-4 py-3 text-right text-sm">Tổng chi:</td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-red-700">
                                                                {formatCurrency(detailItem.tongChi)}
                                                            </td>
                                                            <td colSpan="2"></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>

                                            {/* Tổng kết */}
                                            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 border border-indigo-200">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="text-center">
                                                        <p className="text-sm text-gray-600 mb-1">Tổng thu</p>
                                                        <p className="text-2xl font-bold text-green-600">
                                                            {formatCurrency(detailItem.tongThu)}
                                                        </p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-sm text-gray-600 mb-1">Tổng chi</p>
                                                        <p className="text-2xl font-bold text-red-600">
                                                            {formatCurrency(detailItem.tongChi)}
                                                        </p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-sm text-gray-600 mb-1">Lợi nhuận</p>
                                                        <p className={`text-2xl font-bold ${detailItem.loiNhuan >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                            {formatCurrency(detailItem.loiNhuan)}
                                                        </p>
                                                        <p className="text-sm text-gray-500 mt-1">
                                                            ({detailItem.phanTramLoiNhuan.toFixed(2)}%)
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                            <p className="text-gray-500">Chưa có dữ liệu thu chi</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
                            <button
                                onClick={handleCloseDetailModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">
                                {isEditMode ? 'Cập nhật lô' : 'Thêm lô mới'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Số lô */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Số lô <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={currentQuanLyLo['Số lô']}
                                    onChange={(e) => handleInputChange('Số lô', e.target.value)}
                                    className={`p-2.5 border border-gray-300 rounded-lg w-full ${isEditMode ? 'bg-gray-100' : 'bg-white'}`}
                                    readOnly={isEditMode}
                                    placeholder={isEditMode ? '' : 'Tự động tạo khi chọn ngày và NCC hoặc nhập thủ công'}
                                />
                                {!isEditMode && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Format: LO-YYMMDD-TENVIETATNCC-0001 (số thứ tự tăng dần theo ngày)
                                    </p>
                                )}
                            </div>

                            {/* Ngày mua */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Ngày mua <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={formatDateForInput(currentQuanLyLo['Ngày mua'])}
                                    onChange={(e) => handleDateChange(e.target.value)}
                                    className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                    required
                                    disabled={isEditMode}
                                />
                            </div>

                            {/* Nhà cung cấp */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nhà cung cấp <span className="text-red-500">*</span>
                                </label>

                                {currentQuanLyLo['Nhà cung cấp'] && (
                                    <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Truck className="w-5 h-5 text-green-600" />
                                            <span className="font-medium text-green-800">
                                                {currentQuanLyLo['Nhà cung cấp']}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleNCCChange('')}
                                            className="text-red-600 hover:text-red-800"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}

                                {!currentQuanLyLo['Nhà cung cấp'] && (
                                    <>
                                        <div className="relative mb-2">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Tìm kiếm nhà cung cấp..."
                                                value={searchNCC}
                                                onChange={(e) => setSearchNCC(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>

                                        <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                                            <div className="space-y-2">
                                                {filteredNCCs.length > 0 ? (
                                                    filteredNCCs.map(ncc => (
                                                        <div
                                                            key={ncc.MA_KH}
                                                            onClick={() => handleNCCChange(ncc.TEN_KHACHHANG)}
                                                            className="p-2 hover:bg-indigo-50 rounded cursor-pointer transition-colors border border-transparent hover:border-indigo-200"
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {ncc.TEN_KHACHHANG}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        Mã: {ncc.MA_KH} | Viết tắt: {ncc.TEN_VIET_TAT}
                                                                    </div>
                                                                </div>
                                                                <Check className="w-5 h-5 text-indigo-600 opacity-0 hover:opacity-100" />
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-center py-4 text-gray-500">
                                                        {searchNCC ? 'Không tìm thấy nhà cung cấp nào' : 'Không có nhà cung cấp'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}


                            </div>

                            {/* Khối lượng dự kiến */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Khối lượng dự kiến (tấn) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={currentQuanLyLo['Khối lượng dự kiến (tấn)']}
                                    onChange={(e) => handleInputChange('Khối lượng dự kiến (tấn)', e.target.value)}
                                    className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Nhập khối lượng"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </div>

                            {/* Ghi chú */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Ghi chú
                                </label>
                                <textarea
                                    value={currentQuanLyLo['Ghi chú']}
                                    onChange={(e) => handleInputChange('Ghi chú', e.target.value)}
                                    className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Nhập ghi chú (nếu có)"
                                    rows="3"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
                            <button
                                onClick={handleCloseModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                disabled={isSubmitting}
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSubmitting}
                                className={`px-4 py-2 bg-indigo-600 text-white rounded-lg ${isSubmitting
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-indigo-700'
                                    } flex items-center gap-2 transition-colors shadow-sm`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Đang lưu...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        {isEditMode ? 'Cập nhật' : 'Thêm mới'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmation && itemToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800">Xác nhận xóa</h2>
                            <button
                                onClick={handleCloseDeleteConfirmation}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                <p className="text-red-700">
                                    Bạn có chắc chắn muốn xóa lô này?
                                </p>
                                <div className="mt-3 text-sm text-red-600">
                                    <p><strong>Số lô:</strong> {itemToDelete['Số lô']}</p>
                                    <p><strong>Ngày mua:</strong> {new Date(itemToDelete['Ngày mua']).toLocaleDateString('vi-VN')}</p>
                                    <p><strong>Nhà cung cấp:</strong> {itemToDelete['Nhà cung cấp']}</p>
                                    <p><strong>Khối lượng:</strong> {parseFloat(itemToDelete['Khối lượng dự kiến (tấn)']).toFixed(2)} tấn</p>
                                </div>
                                <p className="text-sm text-red-600 mt-2">
                                    Hành động này không thể hoàn tác.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={handleCloseDeleteConfirmation}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors shadow-sm"
                                >
                                    <Trash className="h-4 w-4" />
                                    Xóa lô
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Container */}
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default QuanLyLoManagement;

